import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Live RCP-3500 paint values driving the `rcp-paint-status` screen. Channel
 * values are the Sony ±99 paint range; the readout strings (ecs, cTemp,
 * mWhite) render verbatim. When the host leaves `paint` unset the screen
 * falls back to the neutral reference defaults.
 */
export interface RcpPaintState {
  white: { r: number; g: number; b: number };
  black: { r: number; g: number; b: number };
  detail: number;
  ecs: string;
  cTemp: string;
  mWhite: string;
}

/**
 * `<panel-display>` — addressable display module primitive.
 *
 * Renders the broadcast display idioms encountered on Phase 1 panels:
 *
 *   - `kind="matrix-green"` — dot-matrix LED, yellow-green emissive,
 *     used for both the eight-character camera identifier readout and
 *     the four-character iris F-number readout on the OCP-400. The
 *     character size is driven by `cqi` against the chassis-frame
 *     container so both displays share an identical cell size.
 *   - `kind="lcd-green"` — backlit alphanumeric LCD with dark text on
 *     a saturated green panel, the menu display on OCP-400.
 *   - `kind="oled-amber" | "oled-cyan" | "oled-red"` — graphical OLED
 *     module for modern panels (CGP-500 status display, future RCP-3500).
 *
 * `mode="placeholder"` renders trustworthy mock data appropriate to
 * the `placeholder` slot. `mode="static"` renders the `text` attribute
 * verbatim. `mode="bound"` is reserved for live state from the
 * pedestal contract surface.
 */
@customElement('panel-display')
export class PanelDisplayElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        /* No container-type here. cqi inside the display passes
         * through to the chassis-frame container, so units like
         * 6cqi resolve against the PANEL width — that gives both
         * dot-matrix displays the same absolute character size
         * regardless of how many cells each display has. */
        inline-size: 100%;
        block-size: 100%;
        font-family: var(--font-data, ui-monospace, monospace);

        --pd-bezel: var(--display-bezel, #050507);
        --pd-glass: var(--display-glass, #0a0a0e);
        --pd-amber: var(--display-amber, #ff9a3c);
        --pd-cyan: var(--display-cyan, #4cd6ff);
        --pd-red: var(--display-red, #ff3a3a);
        --pd-lcd-green: var(--display-lcd-green, #39e639);
        /* Phosphor green matching the soft-button illumination across
         * the panel — same hue, slightly brighter so the dot-matrix
         * readouts read as live LED segments rather than a printed
         * label. */
        --pd-matrix-green: var(--display-matrix-green, #5dff5d);
      }

      .bezel {
        position: relative;
        block-size: 100%;
        inline-size: 100%;
        background: linear-gradient(180deg, var(--pd-bezel), color-mix(in oklab, var(--pd-bezel) 80%, black));
        border-radius: 0.12rem;
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--pd-bezel) 70%, white) / 0.06,
          inset 0 0 0 1px #000;
        padding: 0.08rem;
        display: grid;
        align-content: stretch;
        overflow: hidden;
        box-sizing: border-box;
      }

      .glass {
        position: relative;
        background: linear-gradient(180deg, var(--pd-glass), color-mix(in oklab, var(--pd-glass) 80%, black));
        block-size: 100%;
        inline-size: 100%;
        border-radius: 0.05rem;
        padding: 0.18rem 0.2rem;
        font-variant-numeric: tabular-nums;
        letter-spacing: 0.06em;
        line-height: 1.1;
        display: grid;
        align-content: center;
        justify-content: stretch;
        overflow: hidden;
        box-sizing: border-box;
      }

      .glass::after {
        /* Subtle dot-grid overlay reads as LED pixels on matrix displays. */
        content: '';
        position: absolute;
        inset: 0;
        background-image: radial-gradient(circle, rgba(0, 0, 0, 0.5) 0.3px, transparent 0.6px);
        background-size: 6% 16%;
        opacity: 0;
        pointer-events: none;
      }

      /* Dot-grid overlay deliberately disabled on matrix kinds — at the
       * physically-correct display height (~5.8 mm) on a panel rendered
       * a few hundred pixels tall, the overlay sits on top of the
       * characters at higher density than the characters themselves
       * and turns the readout into illegible fragments. The font and
       * the per-cell layout already read as a matrix display. */

      :host([kind^='matrix-']) .glass {
        font-family: var(--font-data, ui-monospace, monospace);
        font-weight: 700;
        letter-spacing: 0;
        white-space: nowrap;
        padding: 0;
        /* Bypass the base .glass display:grid for matrix kinds — its
         * implicit auto track sizes to content, which lets the inner
         * .matrix-grid grow with the cell font and overflow. Block
         * lay-out keeps the inner grid bounded by the glass height. */
        display: block;
      }

      /* Hardware model: a dot-matrix display is a row of discrete
       * character cells. Each cell renders one character at the
       * intrinsic LED-module size, independent of how many cells the
       * total display has. The 8-character camera-ID readout and the
       * 4-character iris readout share the same module — their cell
       * inline-size is identical; only the display housing width
       * differs (8 modules vs 4 modules side by side).
       *
       * We model this with a CSS grid of N columns, each cell a fixed
       * aspect-ratio (5:7 width:height — the typical character cell
       * proportion on broadcast-grade dot-matrix LEDs), with font-size
       * driven by the cell's own block-size. */
      .matrix-grid {
        display: grid;
        block-size: 100%;
        inline-size: 100%;
        grid-template-rows: minmax(0, 1fr);
        align-items: stretch;
        justify-items: stretch;
        gap: 0;
        /* No container-type here — cqi resolves up the ancestry to
         * the chassis-frame, which IS an inline-size container. That
         * way the character size is a fixed fraction of the panel
         * width and identical across both dot-matrix displays
         * regardless of cell pitch. */
      }

      .matrix-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        inline-size: 100%;
        block-size: 100%;
        /* Absolute character size tied to the panel-frame container,
         * not the per-cell grid track. Same physical LED module on
         * both the cam-ID and iris displays = same on-screen
         * character size. 6cqi against an 82 mm panel ≈ 4.9 mm
         * cap-height, which matches the real LED character cell. */
        font-size: 6cqi;
        line-height: 1;
      }

      /* 7-segment digit cells — real LED segment geometry, not a
       * styled glyph. Segments are SVG polygons drawn per cell with
       * a slight italic skew (broadcast 7-seg lean). seg-on takes
       * the panel's lit colour from --pd-seg-on (Sony amber chain
       * via --illumination-function-on); seg-off keeps the segments
       * faintly visible so the display reads as a fixed-character
       * window even when the value would otherwise leave them
       * dark — alarm-clock style. */
      :host([kind='seven-segment']) .glass {
        /* Segment colour is panel-themeable via --display-seg-on. It is
         * NOT the function-button illumination colour: on Sony RCPs the
         * numeric paint readouts glow green while the adjacent up/down
         * adjustment buttons are amber. A panel that wants amber numerics
         * leaves --display-seg-on unset and falls back to the legacy
         * --illumination-function-on amber. */
        --pd-seg-on: var(--display-seg-on, var(--illumination-function-on, #d4a017));
        --pd-seg-off: color-mix(in oklab, var(--pd-seg-on) 15%, transparent);
        background: linear-gradient(180deg, #0a0a08, #050504);
        padding: 6% 4%;
        box-sizing: border-box;
      }

      .seg-row {
        display: flex;
        align-items: stretch;
        justify-content: center;
        block-size: 100%;
        inline-size: 100%;
        gap: 3%;
      }

      .seg-cell {
        block-size: 100%;
        inline-size: auto;
        aspect-ratio: 100 / 160;
        transform: skewX(-8deg);
      }

      .seg-cell .seg-on {
        fill: var(--pd-seg-on);
        filter: drop-shadow(0 0 0.4px var(--pd-seg-on));
      }

      .seg-cell .seg-off {
        fill: var(--pd-seg-off);
      }

      :host([kind='matrix-green']) .glass {
        /* Tinted dark green that lives in the same colour family as
         * the alphanumeric LCD's backlit glass (#467318 / #355914) but
         * a couple of stops darker — so the cam-id and iris readouts
         * read as siblings of the menu LCD rather than as pure-neon
         * LED panels floating on black. */
        background: linear-gradient(180deg, #1c2a10, #0e1808);
        color: var(--pd-matrix-green);
        /* Slightly tamer phosphor bloom — keeps the LED-emissive
         * character but lets the warmer green-yellow tone show
         * through without the neon-glow halo. */
        text-shadow:
          0 0 0.03rem #ffffff,
          0 0 0.05rem var(--pd-matrix-green),
          0 0 0.12rem color-mix(in oklab, var(--pd-matrix-green) 85%, transparent),
          0 0 0.28rem color-mix(in oklab, var(--pd-matrix-green) 55%, transparent);
      }

      :host([kind='matrix-green']) .bezel {
        background: linear-gradient(180deg, #0e1608, #060a04);
      }

      :host([kind='lcd-green']) .glass {
        /* Backlit alphanumeric LCD: saturated green panel, dark text.
         * Tone sits between the previous dim-olive (#1f3214) and the
         * brightened lime (#5a9a26) — keeps enough green "presence" to
         * read as an LCD filter while preserving text contrast. */
        background: linear-gradient(180deg, #467318, #355914);
        color: #061600;
        font-family: var(--font-data, ui-monospace, monospace);
        /* cqi resolves against the chassis-frame container now. */
        font-size: clamp(0.5rem, 4cqi, 1rem);
        text-shadow: none;
      }

      :host([kind='lcd-green']) .bezel {
        background: linear-gradient(180deg, #283f14, #1a2a0e);
      }

      /* Colour TFT — the CGP-500 LCD. Black background, anti-aliased
       * white type with selective colour fields (red tally bar, blue
       * highlighted cells, yellow ISO bar, channel-tinted value
       * blocks). The bezel sits flush with the chassis-frame; the
       * glass picks up a subtle vignette so the screen reads as a
       * back-lit panel under broadcast key-light. */
      :host([kind='lcd-colour']) .glass {
        background:
          radial-gradient(
            ellipse at 50% 0%,
            #14171c 0%,
            #060709 80%
          );
        color: #f0f3f6;
        font-family: var(--font-data, ui-monospace, monospace);
        font-size: clamp(0.4rem, 2.4cqi, 0.9rem);
        letter-spacing: 0.01em;
        line-height: 1.18;
        padding: 0;
        display: block;
      }

      :host([kind='lcd-colour']) .bezel {
        background: linear-gradient(180deg, #0a0a0c, #050507);
        padding: 0.05rem;
      }

      /* CGP-500 grading placeholder layout. Built as a CSS grid so the
       * card columns and rows scale with the chassis container
       * regardless of how much space the LCD is given. The visual
       * vocabulary matches the macro photographs:
       *   - red tally bar at the top
       *   - large camera-number tile (left), scene-icon tile (centre),
       *     iris-readout tile (right)
       *   - "REF 14" mode-strip beneath
       *   - 4×2 grid of labelled value cards with channel-tinted top
       *     borders and dim grey values
       *   - the lower row gets a slightly muted band so the operator
       *     reads the upper row as the active page
       */
      .cgp-screen {
        display: grid;
        grid-template-rows:
          0.22fr  /* tally */
          1.7fr   /* hero row: cam-id | mode-icon | iris */
          0.55fr  /* ref-strip */
          1.4fr   /* value-card row 1 */
          1.4fr   /* value-card row 2 */;
        block-size: 100%;
        inline-size: 100%;
        gap: 1.2cqi;
        padding: 1.8cqi 1.6cqi;
        box-sizing: border-box;
      }

      .cgp-tally {
        background: linear-gradient(180deg, #d9221d 0%, #8a1411 100%);
        block-size: 100%;
        border-radius: 0.06rem;
        box-shadow:
          inset 0 1px 0 #ff5b55,
          inset 0 -1px 0 #470806;
      }

      .cgp-hero {
        display: grid;
        grid-template-columns: 1fr 1fr 1.1fr;
        gap: 1.2cqi;
        align-items: stretch;
      }

      .cgp-cam {
        display: grid;
        align-content: center;
        justify-content: start;
        padding-inline-start: 1.6cqi;
        font-weight: 800;
        font-size: clamp(1.2rem, 11cqi, 4rem);
        line-height: 0.9;
        letter-spacing: -0.02em;
        color: #ffffff;
      }

      .cgp-mode {
        display: grid;
        place-items: center;
        /* Inline SVG drawn with currentColor so the page-icon picks
         * up the LCD's white text colour without an extra paint pass. */
      }

      .cgp-mode svg {
        block-size: 70%;
        inline-size: auto;
        color: #e8edf4;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.6;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .cgp-iris {
        display: grid;
        align-content: center;
        justify-content: end;
        padding-inline-end: 1.6cqi;
        font-weight: 700;
        font-size: clamp(0.9rem, 7.2cqi, 2.8rem);
        line-height: 0.95;
        letter-spacing: 0;
        color: #ffffff;
      }

      .cgp-ref {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 1.2cqi;
        font-size: clamp(0.45rem, 2.6cqi, 0.9rem);
        color: #b2b8c1;
        padding-inline: 1.4cqi;
      }

      .cgp-ref .badge {
        display: inline-grid;
        place-items: center;
        block-size: 100%;
        padding-inline: 1.4cqi;
        background: #2c3140;
        color: #cfd6e0;
        font-weight: 600;
        font-size: clamp(0.4rem, 2.2cqi, 0.78rem);
        border-radius: 0.05rem;
        letter-spacing: 0.04em;
      }

      .cgp-ref .nav {
        display: inline-flex;
        gap: 0.8cqi;
        color: #5fa8ff;
        font-weight: 600;
      }

      .cgp-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1.1cqi;
      }

      .cgp-card {
        display: grid;
        grid-template-rows: auto 1fr;
        background: #0e1217;
        border-radius: 0.05rem;
        padding: 0.6cqi 1cqi;
        gap: 0.4cqi;
        border-block-start: 1.4cqi solid var(--card-accent, #2c3340);
        min-inline-size: 0;
      }

      .cgp-card .key {
        font-size: clamp(0.32rem, 2cqi, 0.72rem);
        color: #aab1bd;
        letter-spacing: 0.04em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cgp-card .value {
        font-size: clamp(0.5rem, 3.2cqi, 1.05rem);
        color: #f1f4f7;
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .cgp-card.active {
        background: #1b3b7a;
        --card-accent: #2f6fff;
      }

      .cgp-card.active .key { color: #cee0ff; }
      .cgp-card.active .value { color: #ffffff; }

      .cgp-card.warn { --card-accent: #d9221d; }
      .cgp-card.iso { --card-accent: #e8b015; }

      /* Slight luminance dip on the lower grid row so the LCD reads
       * as "active page (top) above a settings tail (bottom)" — the
       * Var/Knee/Gamma row in the macro photos sits visibly muted
       * compared to the Shadow/Mid/High row above it. */
      .cgp-grid.muted {
        opacity: 0.78;
      }

      /* RCP-3500 paint-status screen — Operating Instructions p. 4
       * shows the status page on the 3-inch VGA touch LCD; the
       * clean front elevation (research/sources/photos/rcp-3500-
       * 1200x1200-a.webp) confirms the layout that the operator
       * sees with no menu engaged. Sony's screen vocabulary is
       * deliberately spartan: small white labels, large white
       * numerals, a horizontal red status strip across the top,
       * and a CCU input-level bargraph in the bottom band.
       *
       * Grid rows:
       *   - tally strip (red)
       *   - White R/G/B block with Detail readout on the right
       *   - Black R/G/B block with ECS readout on the right
       *   - bottom strip: C Temp, CAM input level, M White
       */
      /* Sony LCD overrides the lcd-colour vignette palette with a
       * pure-black ground for the rcp-paint-status screen.
       * Powered-on photograph (RCP-3500.avif) shows solid black
       * behind crisp anti-aliased white type — no radial vignette,
       * no card backgrounds, no blue highlight panels. */
      /* Sony LCD — solid black with a subtle corner vignette as
       * shown in the powered-on reference image. Pure-white text,
       * digit value cells underlined with a thin grey rule to read
       * as input fields, amber tint on the C Temp value, and a
       * wide CCU input-level bargraph at the bottom. */
      :host([placeholder='rcp-paint-status']) .glass {
        background:
          radial-gradient(ellipse at 50% 50%, #0a0a0a 0%, #000 75%);
        color: #ffffff;
        padding: 0;
        /* Make the glass its OWN query container so the paint-status
         * typography is measured against the LCD itself, not the chassis
         * frame. Without this, cqi resolves against the ~523 mm-wide panel
         * and the clamp() rem ceilings (e.g. 3.6rem digits) blow the
         * content up to ~292x302 px inside a 102x75 px glass — it spills
         * and clips. Scoped to rcp-paint-status so the CGP-500 lcd-colour
         * screen, which sizes against the panel, is untouched. cqh/cqw
         * below therefore track the glass and the screen scales to fit
         * exactly as the TEST-mode colour bars do. */
        container-type: size;
      }

      .rcp-screen {
        display: grid;
        grid-template-rows:
          1.6fr   /* white block */
          1.6fr   /* black block */
          1fr;    /* bottom strip */
        block-size: 100%;
        inline-size: 100%;
        gap: 2cqi;
        padding: 2cqi 2.2cqi 1.4cqi 2.2cqi;
        box-sizing: border-box;
        color: #ffffff;
        background: transparent;
      }

      .rcp-tally {
        background: linear-gradient(180deg, #d9221d 0%, #8a1411 100%);
        block-size: 100%;
        border-radius: 0.05rem;
        box-shadow:
          inset 0 1px 0 #ff5b55,
          inset 0 -1px 0 #470806;
      }

      .rcp-block {
        display: grid;
        grid-template-columns: 3.2fr 1.8fr;
        gap: 1.4cqi;
        align-items: stretch;
      }

      .rcp-rgb {
        display: grid;
        grid-template-rows: auto auto 1fr;
        gap: 0.4cqi;
      }

      .rcp-block-title {
        font-size: 7cqh;
        font-weight: 500;
        letter-spacing: 0.04em;
        text-align: center;
        color: #ffffff;
      }

      .rcp-rgb-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.6cqi;
      }

      .rcp-rgb-row.head {
        font-size: 5.5cqh;
        color: #aab1bd;
        letter-spacing: 0.08em;
        text-align: center;
      }

      .rcp-rgb-row.value {
        font-size: 17cqh;
        font-weight: 500;
        letter-spacing: 0;
        text-align: center;
        line-height: 1;
        align-items: end;
        color: #ffffff;
      }

      /* Each digit cell carries a thin grey underline so the value
       * reads as a settable input field — matches Sony's status-
       * screen typography. */
      .rcp-rgb-row.value > div,
      .rcp-side-value {
        border-block-end: 1px solid #5a5e63;
        padding-block-end: 0.05rem;
      }

      .rcp-side {
        display: grid;
        grid-template-rows: auto 1fr;
        gap: 0.4cqi;
      }

      .rcp-side-title {
        font-size: 7cqh;
        font-weight: 500;
        letter-spacing: 0.04em;
        text-align: center;
        color: #d5dae0;
      }

      .rcp-side-value {
        font-size: 15cqh;
        font-weight: 600;
        letter-spacing: -0.02em;
        text-align: center;
        line-height: 0.95;
        align-self: end;
        color: #ffffff;
      }

      .rcp-bottom {
        display: grid;
        grid-template-columns: 1fr 1.6fr 1fr;
        gap: 1.4cqi;
        align-items: center;
      }

      .rcp-bottom-cell {
        display: grid;
        grid-template-rows: auto auto;
        gap: 0.2cqi;
        text-align: center;
      }

      .rcp-bottom-label {
        font-size: 5.5cqh;
        color: #aab1bd;
        letter-spacing: 0.06em;
      }

      .rcp-bottom-value {
        font-size: 10cqh;
        color: #ffffff;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      /* C Temp value renders neutral white like the rest of the readout.
       * The clean front-elevation reference (rcp-3500-1200x1200-a.webp)
       * samples the "4300K" glyphs at ~(192,192,192) — no warm cast — so the
       * earlier amber tint taken from the RCP-3500.avif shot is dropped. */
      .rcp-bottom-cell:first-child .rcp-bottom-value {
        color: #ffffff;
      }

      .rcp-bargraph-cell {
        /* CAM and CCU are two independent input-level meters, each its own
         * bar — a label beside a bar, stacked into two rows, exactly as the
         * powered-on reference photograph shows. */
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: center;
        column-gap: 0.8cqi;
        row-gap: 0.8cqh;
      }

      .rcp-bargraph-label {
        font-size: 5.5cqh;
        color: #aab1bd;
        letter-spacing: 0.06em;
      }

      /* Input-level bargraph — twelve discrete cells running cool
       * cyan → green → yellow → red, mirroring Sony's CCU input
       * indicator on the powered-on photograph. The fill level is a
       * static placeholder; future bound state would drive a
       * --rcp-cam-level CSS variable to clamp the visible cell count. */
      .rcp-bargraph {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 0.3cqi;
        block-size: 3cqh;
        align-items: stretch;
      }

      .rcp-bargraph-cell-bar {
        block-size: 100%;
        border-radius: 1px;
      }

      .rcp-bargraph-cell-bar.green { background: #1eaa4a; }
      .rcp-bargraph-cell-bar.yellow { background: #d8b015; }
      .rcp-bargraph-cell-bar.red { background: #d9221d; }
      .rcp-bargraph-cell-bar.off { background: #1f2225; }

      /* Camera-number / tally 8x8 LED dot-matrix module. Unlit
       * dots stay faintly visible as a dark grid so the panel
       * reads as a real LED matrix; lit dots glow saturated red
       * (or amber / green per tally state — toggled via the host's
       * data-tally attribute by future bound state). The lit-state
       * dots have a soft bloom around them, mirroring the real
       * LED illumination pattern in the photographed reference. */
      :host([kind='matrix-red']) .glass,
      :host([kind='matrix-red']) .bezel {
        background: #07060a;
        padding: 0;
      }

      .cam-id {
        display: grid;
        grid-template-rows: repeat(8, 1fr);
        grid-template-columns: repeat(8, 1fr);
        /* Dot pitch measured from the close-up reference photograph:
         * each LED dot fills ~67% of its cell pitch (tight packing,
         * thin inter-dot gaps), with a ~6% margin from the outer dots
         * to the glass edge. A 4% grid gap against the 88% content box
         * reproduces that ~67% fill; padding holds the edge margin. An
         * earlier 6% gap left the dots too sparse (~55% fill). */
        gap: 4%;
        block-size: 100%;
        inline-size: 100%;
        padding: 6%;
        box-sizing: border-box;
        background: #07060a;
      }

      .cam-id-dot {
        border-radius: 50%;
        background: #1c1310;
        box-shadow: inset 0 0 0 0.5px #2a2017;
      }

      .cam-id-dot.lit {
        background: #ff4a18;
        box-shadow:
          0 0 0.05rem #ff8a5a,
          0 0 0.15rem #ff4a18,
          inset 0 0 0 0.5px #ffb088;
      }

      /* Bi-colour partner of .lit — the same diode driven green (tally /
       * lamp-test), matching the red treatment's bloom. */
      .cam-id-dot.lit-green {
        background: #33e23a;
        box-shadow:
          0 0 0.05rem #8dff80,
          0 0 0.15rem #33e23a,
          inset 0 0 0 0.5px #b6ffaa;
      }

      .rows {
        display: grid;
        gap: 0.08rem;
        font-size: inherit;
      }

      .row {
        display: flex;
        justify-content: space-between;
        gap: 0.4rem;
      }

      /*
       * Column-aligned menu grid for the OCP-400 LCD. Four columns,
       * each aligned to the corresponding physical button column (A/B/C/D)
       * below the display — so the ▲/▼ selection arrows under each cell
       * trigger that cell's parameter. Whitespace cells preserve the
       * column structure even where a row has fewer values than slots.
       */
      .menu-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        column-gap: 0.05rem;
        row-gap: 0.08rem;
        text-align: center;
        font-size: clamp(0.5rem, 3cqi, 1rem);
        line-height: 1.05;
      }

      .menu-grid > * {
        min-inline-size: 0;
        overflow: hidden;
        text-overflow: clip;
      }

      /* Lamp-test fills (mode="test"). */
      .pd-bars {
        display: flex;
        inline-size: 100%;
        block-size: 100%;
      }

      .pd-bars > span {
        flex: 1 1 0;
        block-size: 100%;
      }

      .pd-test-fill {
        inline-size: 100%;
        block-size: 100%;
        background: var(--pd-seg-on, #38e828);
      }
    `,
  ];

  @property({ type: String, reflect: true }) accessor mode:
    | 'placeholder'
    | 'static'
    | 'bound'
    | 'off'
    | 'test' = 'placeholder';

  /** Static text content used in `static` mode. */
  @property({ type: String }) accessor text = '';

  /** Display palette and technology selector. */
  @property({ type: String, reflect: true }) accessor kind:
    | 'matrix-green'
    | 'matrix-red'
    | 'seven-segment'
    | 'lcd-green'
    | 'lcd-colour'
    | 'oled-amber'
    | 'oled-cyan'
    | 'oled-red' = 'matrix-green';

  /** Maximum character count for matrix displays; pads with spaces. */
  @property({ type: Number, attribute: 'max-chars' }) accessor maxChars = 0;

  /** Placeholder dataset for `mode="placeholder"`. */
  @property({ type: String }) accessor placeholder:
    | 'camera'
    | 'iris'
    | 'menu'
    | 'cgp-status'
    | 'cgp-grading'
    | 'rcp-paint-status'
    | 'rcp-camera-id'
    | 'none' = 'none';

  /** Live paint values for the RCP-3500 `rcp-paint-status` screen. Set by the
   * panel as its paint dials turn; null renders the neutral reference. */
  @property({ attribute: false }) accessor paint: RcpPaintState | null = null;

  #padToMatrix(value: string): string {
    if (this.maxChars <= 0) return value;
    if (value.length >= this.maxChars) return value.slice(-this.maxChars);
    // Right-justify: Sony's broadcast 7-segments fill from the right
    // edge, so a single-digit value renders as " 1" rather than "1 ".
    // Same convention for the dot-matrix readouts.
    return ' '.repeat(this.maxChars - value.length) + value;
  }

  /**
   * Render a matrix-style display as a grid of N character cells.
   * Each cell holds exactly one character at the LED-module's intrinsic
   * size, so the 8-character camera-ID readout and the 4-character iris
   * readout share an identical visual character size — the only
   * difference between them is how many cells sit side by side.
   */
  #renderMatrix(value: string) {
    const padded = this.#padToMatrix(value);
    const cells = [...padded];
    const columns = this.maxChars > 0 ? this.maxChars : cells.length;
    return html`
      <div
        class="matrix-grid"
        style=${`grid-template-columns: repeat(${columns}, 1fr)`}
      >
        ${cells.map((c) => html`<span class="matrix-cell">${c === ' ' ? ' ' : c}</span>`)}
      </div>
    `;
  }

  /**
   * Render a 7-segment numeric display. Each cell is a self-contained
   * SVG with the seven segments a–g plus a decimal-point circle; the
   * segments lit for the cell's character pick up the panel's
   * `--pd-seg-on` colour (Sony amber via --illumination-function-on),
   * the rest sit at low opacity (--pd-seg-off) so the panel reads as
   * a real alarm-clock LED with fixed character count even when the
   * value would otherwise leave them dark. The italic lean comes
   * from a skewX(-8deg) on each cell.
   */
  #render7Segment(value: string) {
    const padded = this.#padToMatrix(value);
    const cells = [...padded];
    return html`
      <div class="seg-row">
        ${cells.map((c) => this.#render7SegCell(c))}
      </div>
    `;
  }

  #render7SegCell(char: string, all = false) {
    const segments = all ? 'abcdefg' : (SEVEN_SEG_LIT[char] ?? '');
    const showDot = all || char === '.';
    const lit = (s: string) => (segments.includes(s) ? 'on' : 'off');
    return html`
      <svg
        class="seg-cell"
        viewBox="0 0 100 160"
        preserveAspectRatio="xMidYMid meet"
      >
        <polygon class="seg seg-${lit('a')}" points="18,4 82,4 74,16 26,16" />
        <polygon class="seg seg-${lit('b')}" points="84,6 88,72 78,68 76,18" />
        <polygon class="seg seg-${lit('c')}" points="78,92 88,88 84,154 76,142" />
        <polygon class="seg seg-${lit('d')}" points="26,144 74,144 82,156 18,156" />
        <polygon class="seg seg-${lit('e')}" points="16,88 26,92 24,142 12,154" />
        <polygon class="seg seg-${lit('f')}" points="12,6 22,18 24,68 16,72" />
        <polygon class="seg seg-${lit('g')}" points="20,80 26,74 74,74 80,80 74,86 26,86" />
        <circle class="seg seg-${showDot ? 'on' : 'off'} dp" cx="93" cy="148" r="6" />
      </svg>
    `;
  }

  /**
   * Lamp-test fill for a 7-segment display: every cell shows all seven
   * segments plus the decimal point, so the operator can confirm no
   * segment is dead. Cell count follows maxChars (falling back to the
   * static text length, or three).
   */
  #render7SegmentTest() {
    const n = this.maxChars > 0 ? this.maxChars : Math.max(1, this.text.length || 3);
    return html`
      <div class="seg-row">
        ${Array.from({ length: n }, () => this.#render7SegCell('8', true))}
      </div>
    `;
  }

  /**
   * Lamp-test pattern for the camera-number / tally dot-matrix. Reuses the
   * real 8×8 module (.cam-id / .cam-id-dot, the same dots that draw the camera
   * number) so the diodes are the exact physical grid, lit in the exact panel
   * colours — red is the module's own .lit, green its bi-colour partner. The
   * powered unit's test lights a circle: per row, the left diode green and the
   * right diode red, widening to the edges and back. Unlit diodes stay as the
   * dim raster.
   */
  #renderMatrixTest() {
    const N = 8;
    const greenCol = [3, 2, 1, 0, 0, 1, 2, 3];
    const redCol = [4, 5, 6, 7, 7, 6, 5, 4];
    const dots = [];
    for (let row = 0; row < N; row++) {
      for (let col = 0; col < N; col++) {
        const cls = col === greenCol[row] ? 'lit-green' : col === redCol[row] ? 'lit' : '';
        dots.push(html`<div class="cam-id-dot ${cls}"></div>`);
      }
    }
    return html`<div class="cam-id">${dots}</div>`;
  }

  /** Powered-off camera matrix: the full raster, every diode unlit. */
  #renderMatrixOff() {
    return html`
      <div class="cam-id">
        ${Array.from({ length: 64 }, () => html`<div class="cam-id-dot"></div>`)}
      </div>
    `;
  }

  /**
   * Lamp-test fill for the colour LCD: SMPTE-style 75% colour bars
   * (grey, yellow, cyan, green, magenta, red, blue), the classic
   * line-up pattern a powered display shows on test.
   */
  #renderColourBars() {
    const bars = ['#bfbfbf', '#bfbf00', '#00bfbf', '#00bf00', '#bf00bf', '#bf0000', '#0000bf'];
    return html`
      <div class="pd-bars">
        ${bars.map((c) => html`<span style=${`background:${c}`}></span>`)}
      </div>
    `;
  }

  /**
   * CAM / CCU input-level meter. Twelve cells running red → yellow → green
   * left-to-right, matching the powered-on reference photograph where the
   * low end sits red and the headroom green. CAM and CCU are two
   * independent meters, so this renders one bar and is called once per row.
   * Shown full as a static placeholder; bound state would later clamp the
   * lit cell count.
   */
  #renderRcpLevelBar() {
    const colours = [
      'red', 'red', 'red',
      'yellow', 'yellow', 'yellow', 'yellow',
      'green', 'green', 'green', 'green', 'green',
    ];
    return html`
      <div class="rcp-bargraph">
        ${colours.map((c) => html`<div class="rcp-bargraph-cell-bar ${c}"></div>`)}
      </div>
    `;
  }

  #renderPlaceholder() {
    switch (this.placeholder) {
      case 'camera':
        return this.#renderMatrix('CAM 13');
      case 'iris':
        return this.#renderMatrix('F4.0');
      case 'menu':
        // Each row of four cells aligns horizontally to button columns
        // A/B/C/D below the LCD. The ▲/▼ selection arrows under each
        // column trigger the parameter in that column's cell on the
        // corresponding edge row.
        return html`
          <div class="menu-grid">
            <div>Col</div><div>Temp</div><div>Var</div><div>CTemp</div>
            <div>AW1</div><div>4210 K</div><div>99%</div><div>18</div>
            <div></div><div></div><div></div><div></div>
            <div>Saturation</div><div>Tint</div><div></div><div></div>
          </div>
        `;
      case 'cgp-status':
        return html`
          <div class="rows">
            <div class="row"><span>01</span><span>F 13.2</span></div>
            <div class="row"><span>Gain</span><span>3.6 dB</span></div>
            <div class="row"><span>Shutter</span><span>1/50</span></div>
            <div class="row"><span>Col temp</span><span>3200K</span></div>
          </div>
        `;
      case 'rcp-camera-id':
        // 8x8 LED dot-matrix camera-number display. Bitmap below
        // draws the digit "1" centred in the matrix; unlit dots
        // still render as faint dark dots so the operator sees
        // the matrix structure even without an active glyph.
        // Bitmap rows (top-down), 1 = lit dot, 0 = unlit:
        //   . . . . . . . .
        //   . . . . X . . .
        //   . . . X X . . .
        //   . . . . X . . .
        //   . . . . X . . .
        //   . . . . X . . .
        //   . . . X X X . .
        //   . . . . . . . .
        {
          const bitmap = [
            [0, 0, 0, 0, 0, 0, 0, 0],
            [0, 0, 0, 0, 1, 0, 0, 0],
            [0, 0, 0, 1, 1, 0, 0, 0],
            [0, 0, 0, 0, 1, 0, 0, 0],
            [0, 0, 0, 0, 1, 0, 0, 0],
            [0, 0, 0, 0, 1, 0, 0, 0],
            [0, 0, 0, 1, 1, 1, 0, 0],
            [0, 0, 0, 0, 0, 0, 0, 0],
          ];
          return html`
            <div class="cam-id">
              ${bitmap.flatMap((row) => row.map((cell) =>
                html`<div class="cam-id-dot ${cell ? 'lit' : ''}"></div>`,
              ))}
            </div>
          `;
        }
      case 'rcp-paint-status': {
        // RCP-3500 Sony paint-status screen. Traced from the clean front
        // elevation (research/sources/photos/rcp-3500-1200x1200-a.webp):
        // White R/G/B block on the left with the Detail readout on the
        // right, Black R/G/B with the ECS readout on the right, and a
        // bottom band carrying C Temp, the CAM/CCU input bargraphs and the
        // M White offset. The R/G/B and Detail values are live — driven by
        // the panel's paint dials through `paint` — and fall back to the
        // neutral reference reading when it is unset.
        const p = this.paint;
        const v = (n: number | undefined) => String(n ?? 0);
        return html`
          <div class="rcp-screen">
            <div class="rcp-block">
              <div class="rcp-rgb">
                <div class="rcp-block-title">White</div>
                <div class="rcp-rgb-row head">
                  <div>R</div><div>G</div><div>B</div>
                </div>
                <div class="rcp-rgb-row value">
                  <div>${v(p?.white.r)}</div><div>${v(p?.white.g)}</div><div>${v(p?.white.b)}</div>
                </div>
              </div>
              <div class="rcp-side">
                <div class="rcp-side-title">Detail</div>
                <div class="rcp-side-value">${v(p?.detail)}</div>
              </div>
            </div>
            <div class="rcp-block">
              <div class="rcp-rgb">
                <div class="rcp-block-title">Black</div>
                <div class="rcp-rgb-row head">
                  <div>R</div><div>G</div><div>B</div>
                </div>
                <div class="rcp-rgb-row value">
                  <div>${v(p?.black.r)}</div><div>${v(p?.black.g)}</div><div>${v(p?.black.b)}</div>
                </div>
              </div>
              <div class="rcp-side">
                <div class="rcp-side-title">ECS</div>
                <div class="rcp-side-value">${p?.ecs ?? '60.00'}</div>
              </div>
            </div>
            <div class="rcp-bottom">
              <div class="rcp-bottom-cell">
                <div class="rcp-bottom-label">C Temp</div>
                <div class="rcp-bottom-value">${p?.cTemp ?? '4300K'}</div>
              </div>
              <div class="rcp-bargraph-cell">
                <div class="rcp-bargraph-label">CAM</div>
                ${this.#renderRcpLevelBar()}
                <div class="rcp-bargraph-label">CCU</div>
                ${this.#renderRcpLevelBar()}
              </div>
              <div class="rcp-bottom-cell">
                <div class="rcp-bottom-label">M White</div>
                <div class="rcp-bottom-value">${p?.mWhite ?? '0.0'}</div>
              </div>
            </div>
          </div>
        `;
      }
      case 'cgp-grading':
        // Layout traced from the macro photographs of the CGP-500
        // colour LCD: tally bar on top, hero row (cam-id | page-icon |
        // iris-readout), REF mode strip, then two rows of four value
        // cards. The active card highlights in the panel's blue
        // selection colour; the muted bottom row models the inactive
        // settings tail that the operator scrolls into. The page-icon
        // mirrors the engraved icon on the CGA-shortcut row so the
        // operator's mental map stays coherent.
        return html`
          <div class="cgp-screen">
            <div class="cgp-tally"></div>
            <div class="cgp-hero">
              <div class="cgp-cam">01</div>
              <div class="cgp-mode" aria-label="Light page">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 4 L12 1 M12 23 L12 20 M4 12 L1 12 M23 12 L20 12 M6.34 6.34 L4.22 4.22 M19.78 19.78 L17.66 17.66 M6.34 17.66 L4.22 19.78 M19.78 4.22 L17.66 6.34"
                  ></path>
                  <circle cx="12" cy="12" r="5" />
                </svg>
              </div>
              <div class="cgp-iris">F2.8</div>
            </div>
            <div class="cgp-ref">
              <span class="badge">LDX100</span>
              <span>Light · 3 / 4</span>
              <span class="nav">‹ Prv  Nxt ›</span>
            </div>
            <div class="cgp-grid">
              <div class="cgp-card">
                <div class="key">Max. Iris</div>
                <div class="value">F2.8</div>
              </div>
              <div class="cgp-card active">
                <div class="key">M Black</div>
                <div class="value">56</div>
              </div>
              <div class="cgp-card">
                <div class="key">Var ColTmp</div>
                <div class="value">2660 K</div>
              </div>
              <div class="cgp-card">
                <div class="key">Max. Gain</div>
                <div class="value">0 dB</div>
              </div>
            </div>
            <div class="cgp-grid muted">
              <div class="cgp-card">
                <div class="key">Min. Iris</div>
                <div class="value">F8.7</div>
              </div>
              <div class="cgp-card">
                <div class="key">Ext. Iris</div>
                <div class="value">On</div>
              </div>
              <div class="cgp-card">
                <div class="key">Def. Gain</div>
                <div class="value">0 dB</div>
              </div>
              <div class="cgp-card">
                <div class="key">Min. Gain</div>
                <div class="value">−4 dB</div>
              </div>
            </div>
          </div>
        `;
      case 'none':
      default:
        return html``;
    }
  }

  override render() {
    const isMatrix = this.kind.startsWith('matrix-');
    const isSevenSeg = this.kind === 'seven-segment';
    const isLcdColour = this.kind === 'lcd-colour';
    let content;
    if (this.mode === 'off') {
      // Powered-off: the display's physical structure stays visible exactly as
      // when on — the faint segment ghosts, the dim dot raster — but nothing is
      // lit. A TFT/LCD screen has no such resting structure, so it goes dark.
      content = isSevenSeg
        ? this.#render7Segment('')
        : isMatrix
          ? this.#renderMatrixOff()
          : html``;
    } else if (this.mode === 'test') {
      content = isSevenSeg
        ? this.#render7SegmentTest()
        : isMatrix
          ? this.#renderMatrixTest()
          : isLcdColour
            ? this.#renderColourBars()
            : html`<div class="pd-test-fill"></div>`;
    } else if (this.mode === 'placeholder') {
      content = this.#renderPlaceholder();
    } else if (isSevenSeg) {
      content = this.#render7Segment(this.text);
    } else if (isMatrix) {
      content = this.#renderMatrix(this.text);
    } else {
      content = html`<div>${this.maxChars > 0 ? this.#padToMatrix(this.text) : this.text}</div>`;
    }
    return html`
      <div class="bezel">
        <div class="glass">${content}</div>
      </div>
    `;
  }
}

/**
 * Standard 7-segment digit map. Keys are display characters,
 * values are concatenated segment ids drawn from a–g:
 *   a top, b top-right, c bottom-right, d bottom,
 *   e bottom-left, f top-left, g middle.
 */
const SEVEN_SEG_LIT: Record<string, string> = {
  '0': 'abcdef',
  '1': 'bc',
  '2': 'abged',
  '3': 'abgcd',
  '4': 'fgbc',
  '5': 'afgcd',
  '6': 'afgcde',
  '7': 'abc',
  '8': 'abcdefg',
  '9': 'abcdfg',
  '-': 'g',
  ' ': '',
  '.': '',
};

declare global {
  interface HTMLElementTagNameMap {
    'panel-display': PanelDisplayElement;
  }
}
